import { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Bell, BellOff, Clock, Phone, Send, Loader2, MessageSquare, Check, Volume2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import usePushNotifications from '../../hooks/usePushNotifications';
import { useSounds } from '../../hooks/useSounds';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationSettings = () => {
  const { isSupported, isSubscribed, loading: pushLoading, subscribe, unsubscribe, sendTestNotification } = usePushNotifications();
  const { enabled: soundsEnabled, setEnabled: setSoundsEnabled, play: playSound, prime: primeSounds } = useSounds();
  const [settings, setSettings] = useState({
    study_reminders: true,
    due_flashcards: true,
    exam_alerts: true,
    streak_motivation: true,
    weekly_summary: true,
    quiet_hours_start: 22,
    quiet_hours_end: 8,
    reminder_time: 9,
    sms_enabled: false,
    phone_number: ''
  });
  const [saving, setSaving] = useState(false);
  const [smsTestLoading, setSmsTestLoading] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/notifications/settings`);
      setSettings(response.data);
      setPhoneInput(response.data.phone_number || '');
    } catch (error) {
      console.error('Error fetching notification settings:', error);
    }
  };

  const updateSetting = async (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    
    try {
      setSaving(true);
      await axios.put(`${API}/notifications/settings`, { [key]: value });
    } catch (error) {
      console.error('Error updating setting:', error);
      toast.error('Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleNotifications = async () => {
    if (isSubscribed) {
      const success = await unsubscribe();
      if (success) {
        toast.success('Notifications disabled');
      }
    } else {
      const success = await subscribe();
      if (success) {
        toast.success('Notifications enabled! 🔔');
      }
    }
  };

  const handleTestNotification = async () => {
    const result = await sendTestNotification();
    if (result) {
      toast.success(`Test notification sent to ${result.sent_to_devices} device(s)`);
    } else {
      toast.error('Failed to send test notification');
    }
  };

  const savePhoneNumber = async () => {
    const cleaned = phoneInput.replace(/\s/g, '').replace(/-/g, '');
    if (!cleaned || cleaned.length < 9) {
      toast.error('Please enter a valid phone number');
      return;
    }
    try {
      setSaving(true);
      await axios.put(`${API}/notifications/settings`, { phone_number: cleaned });
      setSettings(prev => ({ ...prev, phone_number: cleaned }));
      toast.success('Phone number saved');
    } catch (error) {
      toast.error('Failed to save phone number');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSms = async () => {
    if (!settings.phone_number) {
      toast.error('Save your phone number first');
      return;
    }
    try {
      setSmsTestLoading(true);
      const res = await axios.post(`${API}/notifications/send-test-sms`);
      toast.success(res.data.message || 'Test SMS sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test SMS');
    } finally {
      setSmsTestLoading(false);
    }
  };

  if (!isSupported) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.5 }}
      >
        <Card className="p-6  border-2 bg-muted/20">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
              <BellOff className="w-6 h-6 text-muted-foreground" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Push Notifications</h2>
              <p className="text-sm text-muted-foreground">Not supported in this browser</p>
            </div>
          </div>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.5 }}
    >
      <Card className="p-6  border-2" data-testid="notification-settings">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <Bell className="w-6 h-6 text-amber-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Push Notifications</h2>
              <p className="text-sm text-muted-foreground">Study reminders & alerts</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              checked={isSubscribed}
              onCheckedChange={handleToggleNotifications}
              disabled={pushLoading}
              data-testid="notifications-toggle"
            />
            <span className={`text-sm font-medium ${isSubscribed ? 'text-green-500' : 'text-muted-foreground'}`}>
              {isSubscribed ? 'On' : 'Off'}
            </span>
          </div>
        </div>

        {isSubscribed && (
          <div className="space-y-6">
            {/* Notification Types */}
            <div className="space-y-4">
              <Label className="text-sm font-semibold">Notification Types</Label>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-xl bg-muted/30">
                  <div>
                    <p className="font-medium">Study Reminders</p>
                    <p className="text-xs text-muted-foreground">Daily study time notifications</p>
                  </div>
                  <Switch
                    checked={settings.study_reminders}
                    onCheckedChange={(v) => updateSetting('study_reminders', v)}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 rounded-xl bg-muted/30">
                  <div>
                    <p className="font-medium">Due Flashcards</p>
                    <p className="text-xs text-muted-foreground">Alerts when reviews are due</p>
                  </div>
                  <Switch
                    checked={settings.due_flashcards}
                    onCheckedChange={(v) => updateSetting('due_flashcards', v)}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 rounded-xl bg-muted/30">
                  <div>
                    <p className="font-medium">Exam Alerts</p>
                    <p className="text-xs text-muted-foreground">Reminders before exams</p>
                  </div>
                  <Switch
                    checked={settings.exam_alerts}
                    onCheckedChange={(v) => updateSetting('exam_alerts', v)}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 rounded-xl bg-muted/30">
                  <div>
                    <p className="font-medium">Streak Motivation</p>
                    <p className="text-xs text-muted-foreground">Keep your study streak alive</p>
                  </div>
                  <Switch
                    checked={settings.streak_motivation}
                    onCheckedChange={(v) => updateSetting('streak_motivation', v)}
                  />
                </div>

                <div className="flex items-center justify-between p-3 rounded-xl bg-muted/30">
                  <div>
                    <p className="font-medium">Study Plan Tasks</p>
                    <p className="text-xs text-muted-foreground">Reminders for upcoming study plan sessions</p>
                  </div>
                  <Switch
                    checked={settings.study_plan_reminders !== false}
                    onCheckedChange={(v) => updateSetting('study_plan_reminders', v)}
                    data-testid="study-plan-reminders-toggle"
                  />
                </div>

                {/* iter193 — subtle sound cues toggle was here originally
                    but it's now hoisted to its own sibling Card below so
                    free users without push subscribed can still toggle it
                    (the cues are pure client-side Web Audio). */}
              </div>
            </div>

            {/* Reminder Time */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Daily Reminder Time
              </Label>
              <div className="flex items-center gap-4">
                <select
                  value={settings.reminder_time}
                  onChange={(e) => updateSetting('reminder_time', parseInt(e.target.value))}
                  className="w-full p-3 rounded-xl border bg-background"
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={`opt-${i}`} value={i}>
                      {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i-12}:00 PM`}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Quiet Hours */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold">Quiet Hours (No Notifications)</Label>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground mb-1">From</p>
                  <select
                    value={settings.quiet_hours_start}
                    onChange={(e) => updateSetting('quiet_hours_start', parseInt(e.target.value))}
                    className="w-full p-2 rounded-lg border bg-background text-sm"
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={`opt-${i}`} value={i}>
                        {i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i-12} PM`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground mb-1">To</p>
                  <select
                    value={settings.quiet_hours_end}
                    onChange={(e) => updateSetting('quiet_hours_end', parseInt(e.target.value))}
                    className="w-full p-2 rounded-lg border bg-background text-sm"
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={`opt-${i}`} value={i}>
                        {i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i-12} PM`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Test Button */}
            <Button
              onClick={handleTestNotification}
              variant="outline"
              className="w-full rounded-xl"
              data-testid="test-notification-btn"
            >
              <Bell className="w-4 h-4 mr-2" />
              Send Test Notification
            </Button>
          </div>
        )}

        {!isSubscribed && (
          <div className="text-center py-6">
            <BellOff className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground mb-4">
              Enable notifications to receive study reminders and flashcard alerts
            </p>
            <Button onClick={handleToggleNotifications} disabled={pushLoading} className="rounded-full">
              {pushLoading ? 'Enabling...' : 'Enable Notifications'}
            </Button>
          </div>
        )}
      </Card>

      {/* iter193 — Subtle sound cues. Standalone Card so it's reachable
          REGARDLESS of push-subscription state (Web Audio API + localStorage
          have nothing to do with browser push notifications). */}
      <Card className="p-6 border-2 mt-6" data-testid="sound-cues-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#002FA7]/10 flex items-center justify-center">
              <Volume2 className="w-6 h-6 text-[#002FA7]" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Subtle sound cues</h2>
              <p className="text-sm text-muted-foreground max-w-md">
                Soft synth chimes on lecture-saved, card-correct, streak-saved, and achievement unlocks. Generated in the browser — no audio files.
              </p>
            </div>
          </div>
          <Switch
            checked={soundsEnabled}
            onCheckedChange={async (v) => {
              setSoundsEnabled(v);
              if (v) {
                // iOS Safari requires a user gesture to unlock audio —
                // the toggle click IS that gesture, so prime + play a
                // sample so the user hears what they just enabled.
                await primeSounds();
                setTimeout(() => playSound('lecture-saved'), 60);
              }
            }}
            data-testid="sound-cues-toggle"
          />
        </div>
      </Card>

      {/* SMS Notifications via Hubtel */}
      <Card className="p-6  border-2 mt-6" data-testid="sms-notification-settings">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold">SMS Notifications</h2>
              <p className="text-sm text-muted-foreground">Get reminders via text message (Hubtel)</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              checked={settings.sms_enabled || false}
              onCheckedChange={(v) => updateSetting('sms_enabled', v)}
              data-testid="sms-toggle"
            />
            <span className={`text-sm font-medium ${settings.sms_enabled ? 'text-green-500' : 'text-muted-foreground'}`}>
              {settings.sms_enabled ? 'On' : 'Off'}
            </span>
          </div>
        </div>

        {settings.sms_enabled && (
          <div className="space-y-5">
            {/* Phone Number Input */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Phone Number
              </Label>
              <p className="text-xs text-muted-foreground">Enter your number with country code (e.g. 233246257382 or 0246257382)</p>
              <div className="flex gap-2">
                <Input
                  type="tel"
                  value={phoneInput}
                  onChange={(e) => setPhoneInput(e.target.value)}
                  placeholder="e.g. 0246257382"
                  className="flex-1 rounded-xl"
                  data-testid="sms-phone-input"
                />
                <Button
                  onClick={savePhoneNumber}
                  disabled={saving || !phoneInput}
                  className="rounded-xl"
                  data-testid="save-phone-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                </Button>
              </div>
              {settings.phone_number && (
                <p className="text-xs text-green-500">Saved: {settings.phone_number}</p>
              )}
            </div>

            {/* SMS notification types info */}
            <div className="p-3 rounded-xl bg-muted/30">
              <p className="text-sm font-medium mb-2">SMS will be sent for:</p>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                  Study reminders
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                  Due flashcard alerts
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                  Exam reminders
                </li>
              </ul>
            </div>

            {/* Test SMS Button */}
            <Button
              onClick={handleTestSms}
              variant="outline"
              className="w-full rounded-xl"
              disabled={smsTestLoading || !settings.phone_number}
              data-testid="test-sms-btn"
            >
              {smsTestLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Test SMS
            </Button>
          </div>
        )}

        {!settings.sms_enabled && (
          <div className="text-center py-4">
            <p className="text-sm text-muted-foreground">
              Turn on SMS to receive study reminders as text messages
            </p>
          </div>
        )}
      </Card>
    </motion.div>
  );
};

export default NotificationSettings;
